import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  BarChart3,
  CalendarDays,
  Clock3,
  FileCheck2,
  Gauge,
  LogOut,
  Plane,
  TimerOff,
  Users,
} from "lucide-react";
import type { AdminStatisticsData, AdminUserStatistic } from "../types/admin-panel";

type Props = {
  statistics: AdminStatisticsData | null;
  selectedYear: number;
  loading: boolean;
  onChangeYear: (year: number) => Promise<void> | void;
};

type DonutSegment = {
  label: string;
  value: number;
  color: string;
};

export function StatisticsPanel({
  statistics,
  selectedYear,
  loading,
  onChangeYear,
}: Props) {
  const [selectedUser, setSelectedUser] = useState("all");
  const [selectedRole, setSelectedRole] = useState("all");
  const [selectedDepartment, setSelectedDepartment] = useState("all");

  const items = statistics?.items ?? [];
  const roleOptions = useMemo(
    () =>
      Array.from(new Set(items.map((item) => item.rol).filter(Boolean))).sort(
        (left, right) => roleLabel(left).localeCompare(roleLabel(right), "es"),
      ),
    [items],
  );
  const departmentOptions = useMemo(
    () =>
      Array.from(
        new Set(
          items
            .map((item) => item.departamento?.trim())
            .filter((value): value is string => Boolean(value)),
        ),
      ).sort((left, right) => left.localeCompare(right, "es")),
    [items],
  );

  const usersMatchingContext = useMemo(
    () =>
      items.filter((item) => {
        const matchesRole = selectedRole === "all" || item.rol === selectedRole;
        const matchesDepartment =
          selectedDepartment === "all" || item.departamento === selectedDepartment;
        return matchesRole && matchesDepartment;
      }),
    [items, selectedDepartment, selectedRole],
  );

  useEffect(() => {
    if (
      selectedUser !== "all" &&
      !usersMatchingContext.some((item) => item.usuario_id === Number(selectedUser))
    ) {
      setSelectedUser("all");
    }
  }, [selectedUser, usersMatchingContext]);

  const filteredItems = useMemo(
    () =>
      usersMatchingContext.filter(
        (item) => selectedUser === "all" || item.usuario_id === Number(selectedUser),
      ),
    [selectedUser, usersMatchingContext],
  );

  const totals = useMemo(() => {
    const scheduled = sum(filteredItems, "scheduled_days");
    const clocked = sum(filteredItems, "clocked_days");

    return {
      users: filteredItems.length,
      scheduled,
      clocked,
      attendanceRate: scheduled > 0 ? Math.round((clocked / scheduled) * 100) : 0,
      absences: sum(filteredItems, "absences"),
      late: sum(filteredItems, "late_count"),
      earlyExit: sum(filteredItems, "early_exit_count"),
      pendingJustifications: sum(filteredItems, "justifications_pending"),
      approvedJustifications: sum(filteredItems, "justifications_approved"),
      rejectedJustifications: sum(filteredItems, "justifications_rejected"),
      approvedVacation: sum(filteredItems, "vacation_approved_days"),
      pendingVacation: sum(filteredItems, "vacation_pending_days"),
      rejectedVacation: sum(filteredItems, "vacation_rejected_days"),
      remainingVacation: sum(filteredItems, "vacation_remaining_days"),
    };
  }, [filteredItems]);

  const incidentChartItems = useMemo(
    () =>
      [...filteredItems]
        .sort(
          (left, right) =>
            right.absences + right.late_count + right.early_exit_count -
            (left.absences + left.late_count + left.early_exit_count),
        )
        .slice(0, 8),
    [filteredItems],
  );

  const availableYears = statistics?.available_years?.length
    ? statistics.available_years
    : [selectedYear];

  return (
    <div className="flex flex-col gap-6">
      <section className="rounded-[32px] border border-[#e7ecef] bg-white p-6 shadow-[0_20px_50px_rgba(15,23,42,.06)]">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-emerald-700">
              <BarChart3 size={14} />
              Estadísticas de usuarios
            </div>
            <h2 className="mt-4 text-3xl font-black tracking-tight text-slate-950">
              Asistencia y vacaciones
            </h2>
            <p className="mt-2 max-w-3xl text-sm font-semibold leading-6 text-slate-500">
              Resumen anual de asistencia, incidencias de fichaje, justificaciones y saldo de vacaciones por usuario.
            </p>
          </div>

          <div className="grid w-full gap-3 sm:grid-cols-2 xl:w-auto xl:min-w-[720px] xl:grid-cols-4">
            <FilterField label="Usuario">
              <select
                value={selectedUser}
                onChange={(event) => setSelectedUser(event.target.value)}
                className="statistics-filter-control"
              >
                <option value="all">Todos los usuarios</option>
                {usersMatchingContext.map((item) => (
                  <option key={item.usuario_id} value={item.usuario_id}>
                    {item.empleado}
                  </option>
                ))}
              </select>
            </FilterField>

            <FilterField label="Rol">
              <select
                value={selectedRole}
                onChange={(event) => setSelectedRole(event.target.value)}
                className="statistics-filter-control"
              >
                <option value="all">Todos los roles</option>
                {roleOptions.map((role) => (
                  <option key={role} value={role}>
                    {roleLabel(role)}
                  </option>
                ))}
              </select>
            </FilterField>

            <FilterField label="Departamento">
              <select
                value={selectedDepartment}
                onChange={(event) => setSelectedDepartment(event.target.value)}
                className="statistics-filter-control"
              >
                <option value="all">Todos</option>
                {departmentOptions.map((department) => (
                  <option key={department} value={department}>
                    {department}
                  </option>
                ))}
              </select>
            </FilterField>

            <FilterField label="Año">
              <select
                value={selectedYear}
                disabled={loading}
                onChange={(event) => void onChangeYear(Number(event.target.value))}
                className="statistics-filter-control disabled:opacity-60"
              >
                {availableYears.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </FilterField>
          </div>
        </div>
      </section>

      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-8">
        <StatMetric label="Usuarios" value={totals.users} icon={Users} />
        <StatMetric
          label="Asistencia media"
          value={`${totals.attendanceRate}%`}
          icon={Gauge}
          tone="sky"
        />
        <StatMetric label="Faltas" value={totals.absences} icon={TimerOff} tone="rose" />
        <StatMetric label="Retrasos" value={totals.late} icon={Clock3} tone="amber" />
        <StatMetric
          label="Salidas tempranas"
          value={totals.earlyExit}
          icon={LogOut}
          tone="rose"
        />
        <StatMetric
          label="Justificaciones pendientes"
          value={totals.pendingJustifications}
          icon={FileCheck2}
          tone="amber"
        />
        <StatMetric
          label="Vacaciones aprobadas"
          value={formatDays(totals.approvedVacation)}
          icon={Plane}
        />
        <StatMetric
          label="Saldo disponible"
          value={formatDays(totals.remainingVacation)}
          icon={CalendarDays}
          tone="sky"
        />
      </section>

      <section className="grid gap-4 lg:grid-cols-2">
        <DonutChart
          title="Estado de justificaciones"
          subtitle="Distribución sobre los usuarios filtrados."
          centerLabel="Total"
          centerValue={
            totals.pendingJustifications +
            totals.approvedJustifications +
            totals.rejectedJustifications
          }
          segments={[
            { label: "Justificadas", value: totals.approvedJustifications, color: "#059669" },
            { label: "Pendientes", value: totals.pendingJustifications, color: "#d97706" },
            { label: "No justificadas", value: totals.rejectedJustifications, color: "#e11d48" },
          ]}
        />

        <DonutChart
          title="Vacaciones solicitadas"
          subtitle="Días por estado durante el año seleccionado."
          centerLabel="Solicitados"
          centerValue={formatDays(
            totals.approvedVacation + totals.pendingVacation + totals.rejectedVacation,
          )}
          segments={[
            { label: "Aprobadas", value: totals.approvedVacation, color: "#059669" },
            { label: "Pendientes", value: totals.pendingVacation, color: "#0284c7" },
            { label: "Rechazadas", value: totals.rejectedVacation, color: "#e11d48" },
          ]}
        />
      </section>

      <IncidentBars items={incidentChartItems} loading={loading} />

      <section className="overflow-hidden rounded-[32px] border border-[#e7ecef] bg-white shadow-[0_20px_50px_rgba(15,23,42,.06)]">
        <div className="border-b border-slate-100 px-6 py-5">
          <div className="text-lg font-black text-slate-950">Detalle por usuario</div>
          <div className="mt-1 text-sm font-semibold text-slate-500">
            Los datos de asistencia corresponden al año seleccionado; el saldo incluye el carry disponible cuando corresponde.
          </div>
        </div>

        <div className="max-h-[620px] overflow-auto">
          <table className="w-full min-w-[1580px] border-separate border-spacing-0">
            <thead className="sticky top-0 z-10 bg-slate-50">
              <tr>
                {[
                  "Usuario",
                  "Rol",
                  "Programados",
                  "Fichados",
                  "Faltas",
                  "Retrasos",
                  "Salidas tempranas",
                  "Justificaciones",
                  "Vac. aprobadas",
                  "Vac. pendientes",
                  "Vac. solicitadas",
                  "Saldo restante",
                ].map((label) => (
                  <th
                    key={label}
                    className="border-b border-slate-200 bg-slate-50 px-4 py-3 text-left text-xs font-black uppercase tracking-[0.12em] text-slate-400"
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={12} className="px-6 py-16 text-center text-sm font-bold text-slate-400">
                    Cargando estadísticas...
                  </td>
                </tr>
              ) : filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={12} className="px-6 py-16 text-center text-sm font-bold text-slate-400">
                    No hay estadísticas para mostrar con los filtros seleccionados.
                  </td>
                </tr>
              ) : (
                filteredItems.map((item) => <StatisticRow key={item.usuario_id} item={item} />)
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function FilterField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="min-w-0">
      <span className="mb-2 block text-xs font-black uppercase tracking-[0.14em] text-slate-400">
        {label}
      </span>
      {children}
    </label>
  );
}

function IncidentBars({ items, loading }: { items: AdminUserStatistic[]; loading: boolean }) {
  const maxValue = Math.max(
    1,
    ...items.flatMap((item) => [item.absences, item.late_count, item.early_exit_count]),
  );

  return (
    <section className="rounded-[32px] border border-[#e7ecef] bg-white p-5 shadow-[0_20px_50px_rgba(15,23,42,.06)]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-lg font-black text-slate-950">Incidencias de asistencia</div>
          <div className="mt-1 text-sm font-semibold text-slate-500">
            Usuarios con más faltas, retrasos y salidas tempranas según los filtros activos.
          </div>
        </div>
        <div className="flex flex-wrap gap-3 text-[11px] font-black text-slate-500">
          <ChartLegend label="Faltas" color="#e11d48" />
          <ChartLegend label="Retrasos" color="#d97706" />
          <ChartLegend label="Salidas" color="#0284c7" />
        </div>
      </div>

      <div className="dashboard-chat-scroll mt-5 grid max-h-[264px] gap-4 overflow-y-auto pr-2">
        {loading ? (
          <div className="py-14 text-center text-sm font-bold text-slate-400">Cargando gráfico...</div>
        ) : items.length === 0 ? (
          <div className="py-14 text-center text-sm font-bold text-slate-400">No hay datos para representar.</div>
        ) : (
          items.map((item) => (
            <div key={item.usuario_id} className="grid min-h-[54px] gap-2 md:grid-cols-[170px_minmax(0,1fr)] md:items-center">
              <div className="min-w-0">
                <div className="truncate text-sm font-black text-slate-800">{item.empleado}</div>
                <div className="truncate text-[11px] font-bold text-slate-400">
                  {roleLabel(item.rol)}{item.departamento ? ` · ${item.departamento}` : ""}
                </div>
              </div>
              <div className="grid gap-1.5">
                <MetricBar value={item.absences} max={maxValue} color="#e11d48" />
                <MetricBar value={item.late_count} max={maxValue} color="#d97706" />
                <MetricBar value={item.early_exit_count} max={maxValue} color="#0284c7" />
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
}

function MetricBar({ value, max, color }: { value: number; max: number; color: string }) {
  const width = value === 0 ? 0 : Math.max(4, (value / max) * 100);

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_34px] items-center gap-2">
      <div className="h-2.5 overflow-hidden rounded-full bg-slate-100">
        <div
          className="h-full rounded-full transition-[width] duration-300"
          style={{ width: `${width}%`, backgroundColor: color }}
        />
      </div>
      <span className="text-right text-[11px] font-black text-slate-500">{value}</span>
    </div>
  );
}

function DonutChart({
  title,
  subtitle,
  segments,
  centerLabel,
  centerValue,
}: {
  title: string;
  subtitle: string;
  segments: DonutSegment[];
  centerLabel: string;
  centerValue: string | number;
}) {
  const total = segments.reduce((accumulator, segment) => accumulator + segment.value, 0);
  let cursor = 0;
  const stops = segments
    .filter((segment) => segment.value > 0)
    .map((segment) => {
      const start = cursor;
      cursor += total > 0 ? (segment.value / total) * 100 : 0;
      return `${segment.color} ${start}% ${cursor}%`;
    });
  const background = total > 0
    ? `conic-gradient(${stops.join(", ")})`
    : "conic-gradient(#e2e8f0 0% 100%)";

  return (
    <section className="rounded-[32px] border border-[#e7ecef] bg-white p-5 shadow-[0_20px_50px_rgba(15,23,42,.06)]">
      <div className="text-base font-black text-slate-950">{title}</div>
      <div className="mt-1 text-xs font-semibold leading-5 text-slate-500">{subtitle}</div>

      <div className="mt-5 flex items-center gap-5">
        <div className="relative h-32 w-32 shrink-0 rounded-full" style={{ background }}>
          <div className="absolute inset-[18px] grid place-items-center rounded-full border border-slate-100 bg-white text-center shadow-inner">
            <div>
              <div className="text-xl font-black text-slate-950">{centerValue}</div>
              <div className="mt-0.5 text-[9px] font-black uppercase tracking-[0.12em] text-slate-400">
                {centerLabel}
              </div>
            </div>
          </div>
        </div>

        <div className="min-w-0 flex-1 space-y-2.5">
          {segments.map((segment) => (
            <div key={segment.label} className="flex items-center justify-between gap-3 text-xs">
              <span className="flex min-w-0 items-center gap-2 font-bold text-slate-600">
                <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: segment.color }} />
                <span className="truncate">{segment.label}</span>
              </span>
              <span className="font-black text-slate-800">{formatCompactValue(segment.value)}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ChartLegend({ label, color }: { label: string; color: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
      {label}
    </span>
  );
}

function StatisticRow({ item }: { item: AdminUserStatistic }) {
  const attendanceRate = item.scheduled_days > 0
    ? Math.round((item.clocked_days / item.scheduled_days) * 100)
    : 0;

  return (
    <tr>
      <td className="border-b border-slate-100 px-4 py-4">
        <div className="font-black text-slate-950">{item.empleado}</div>
        <div className="mt-1 text-xs font-semibold text-slate-500">{item.correo}</div>
        {item.departamento && (
          <div className="mt-1 text-xs font-bold text-emerald-600">{item.departamento}</div>
        )}
      </td>
      <td className="border-b border-slate-100 px-4 py-4 text-sm font-black text-slate-700">
        {roleLabel(item.rol)}
      </td>
      <NumberCell value={item.scheduled_days} />
      <td className="border-b border-slate-100 px-4 py-4 text-sm font-black text-slate-700">
        {item.clocked_days}
        <div className="mt-1 text-xs font-bold text-slate-400">{attendanceRate}% asistencia</div>
      </td>
      <NumberCell value={item.absences} alert={item.absences > 0} />
      <NumberCell value={item.late_count} alert={item.late_count > 0} />
      <NumberCell value={item.early_exit_count} alert={item.early_exit_count > 0} />
      <td className="border-b border-slate-100 px-4 py-4 text-sm font-bold text-slate-600">
        <div>{item.justifications_total} total</div>
        <div className="mt-1 text-xs text-amber-600">{item.justifications_pending} pendientes</div>
        <div className="mt-1 text-xs text-emerald-600">{item.justifications_approved} justificadas</div>
        <div className="mt-1 text-xs text-rose-600">{item.justifications_rejected} no justificadas</div>
      </td>
      <DaysCell value={item.vacation_approved_days} />
      <DaysCell value={item.vacation_pending_days} />
      <td className="border-b border-slate-100 px-4 py-4 text-sm font-black text-slate-700">
        {formatDays(item.vacation_requested_days)}
        <div className="mt-1 text-xs font-bold text-slate-400">
          Base anual {formatDays(item.vacation_annual_days)}
        </div>
        {item.vacation_rejected_days > 0 && (
          <div className="mt-1 text-xs font-bold text-rose-600">
            {formatDays(item.vacation_rejected_days)} rechazadas
          </div>
        )}
      </td>
      <td className="border-b border-slate-100 px-4 py-4 text-sm font-black text-emerald-700">
        {formatDays(item.vacation_remaining_days)}
        <div className="mt-1 text-xs font-bold text-slate-400">
          {formatDays(item.vacation_remaining_current_days)} actuales
        </div>
        {item.vacation_remaining_carry_days > 0 && (
          <div className="mt-1 text-xs font-bold text-sky-600">
            + {formatDays(item.vacation_remaining_carry_days)} carry
          </div>
        )}
      </td>
    </tr>
  );
}

function NumberCell({ value, alert = false }: { value: number; alert?: boolean }) {
  return (
    <td className={`border-b border-slate-100 px-4 py-4 text-sm font-black ${alert ? "text-rose-700" : "text-slate-700"}`}>
      {value}
    </td>
  );
}

function DaysCell({ value }: { value: number }) {
  return (
    <td className="border-b border-slate-100 px-4 py-4 text-sm font-black text-slate-700">
      {formatDays(value)}
    </td>
  );
}

function StatMetric({
  label,
  value,
  icon: Icon,
  tone = "emerald",
}: {
  label: string;
  value: number | string;
  icon: typeof Users;
  tone?: "emerald" | "rose" | "amber" | "sky";
}) {
  const classes = {
    emerald: "border-emerald-100 bg-emerald-50 text-emerald-700",
    rose: "border-rose-100 bg-rose-50 text-rose-700",
    amber: "border-amber-100 bg-amber-50 text-amber-700",
    sky: "border-sky-100 bg-sky-50 text-sky-700",
  };

  return (
    <div className="rounded-[26px] border border-slate-200 bg-white p-4 shadow-[0_14px_35px_rgba(15,23,42,.05)]">
      <div className={`grid h-10 w-10 place-items-center rounded-2xl border ${classes[tone]}`}>
        <Icon size={18} />
      </div>
      <div className="mt-4 text-2xl font-black text-slate-950">{value}</div>
      <div className="mt-1 text-xs font-black uppercase tracking-[0.12em] text-slate-400">{label}</div>
    </div>
  );
}

function sum(items: AdminUserStatistic[], key: keyof AdminUserStatistic): number {
  return items.reduce((total, item) => {
    const value = item[key];
    return total + (typeof value === "number" ? value : 0);
  }, 0);
}

function roleLabel(role: string): string {
  if (role === "admin") return "Administrador";
  if (role === "rrhh") return "RRHH";
  return role
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatDays(value: number): string {
  const formatted = Number.isInteger(value) ? String(value) : value.toFixed(1).replace(".", ",");
  return `${formatted} d`;
}

function formatCompactValue(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1).replace(".", ",");
}
